import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    KeyRound,
    FileCheck2,
    ArrowRight,
    Lock,
    Database,
    Network,
    Eye,
} from "lucide-react";
import { Layout } from "@/components/Layout";

const PILLARS = [
    {
        icon: Lock,
        title: "Custody stays local",
        body: "Each institution retains its own evidence and discloses only the minimum reference needed for the governed action.",
    },
    {
        icon: KeyRound,
        title: "Authority is role-bound",
        body: "A role wallet may request, approve, freeze or release an action only within its assigned policy and delegated authority.",
    },
    {
        icon: FileCheck2,
        title: "Receipts are independently verifiable",
        body: "The KATLAS node signs and verifies what happened without exposing the underlying private evidence.",
    },
];

const WHY = [
    {
        icon: Database,
        title: "Not a central data platform",
        body: "Raw evidence is not pooled into a shared application or public ledger.",
    },
    {
        icon: Network,
        title: "Not token-specific logic",
        body: "The control follows the governed action across token types, ledgers and banking workflows.",
    },
    {
        icon: ShieldCheck,
        title: "Not retrospective audit",
        body: "The control envelope determines whether the action may proceed before the execution adapter is invoked.",
    },
    {
        icon: Eye,
        title: "Not a black-box compliance score",
        body: "The decision identifies the acting role, policy boundary, evidence-reference state and accountable outcome.",
    },
];

export default function Landing() {
    return (
        <Layout>
            {/* Hero */}
            <section className="fade-up">
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                    APIX / Swift digital-assets challenge · Technical demonstration sandbox
                </p>
                <h1 className="mt-5 max-w-4xl text-4xl font-medium leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
                    Every token action carries proof of who authorised it.
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
                    Roles decide under their own policies. Evidence never leaves their
                    custody. KATLAS adds a shared, verifiable layer of decision state -
                    replay-proof authorisations any execution environment can consume to
                    judge whether an asset action is trustworthy. No central gatekeeper.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link
                        to="/live"
                        data-testid="cta-live-engine"
                        className="group inline-flex items-center gap-2 rounded-sm bg-blue-950 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                    >
                        Run the live engine
                        <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                        to="/request/CA-ELEC-001"
                        data-testid="cta-explore-ca"
                        className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        Explore corporate-action election
                    </Link>
                    <Link
                        to="/standards"
                        data-testid="cta-structured-data"
                        className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        See structured business data
                    </Link>
                    <Link
                        to="/enforcement"
                        data-testid="cta-enforcement"
                        className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        Where enforcement lives
                    </Link>
                    <Link
                        to="/explained"
                        data-testid="cta-explained"
                        className="inline-flex items-center rounded-sm px-2 py-2.5 text-sm text-slate-500 underline underline-offset-2 transition-colors duration-150 hover:text-slate-900"
                    >
                        New here? Read the plain-English overview
                    </Link>
                </div>

                {/* Governance pillars */}
                <div className="mt-12 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 md:grid-cols-3">
                    {PILLARS.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.06 * i, duration: 0.3 }}
                            data-testid={`pillar-${i}`}
                            className="bg-white p-6"
                        >
                            <c.icon className="h-5 w-5 text-blue-950" strokeWidth={1.5} />
                            <h3 className="mt-4 text-lg font-medium text-slate-900">{c.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.body}</p>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-4 text-xs leading-relaxed text-slate-400">
                    Governance boundary: evidence retained by role wallets; only
                    references and authorised decision metadata enter the shared control
                    flow.
                </p>
            </section>

            {/* What makes this more than a control dashboard - substrate */}
            <section className="mt-16" data-testid="governance-substrate">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    What makes this more than a control dashboard
                </h2>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-2">
                    <div className="bg-white p-6">
                        <dl className="space-y-4">
                            <SubstrateItem term="Institutional Role Wallets" desc="retain each participant’s private evidence, credentials, policy context and signing capability." />
                            <SubstrateItem term="Role-to-Role Lifecycle Protocols" desc="govern the permitted handoff between accountable roles." />
                            <SubstrateItem term="The Control Decision Envelope" desc="carries the minimum authority, policy and evidence-reference state required to determine whether an action may proceed." />
                            <SubstrateItem term="KATLAS shared proof" desc="independently attests to the governed outcome without pooling raw private evidence into a central application or ledger." />
                        </dl>
                    </div>
                    <div className="bg-white p-6">
                        <div className="flex flex-col gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                            <SubstrateLayer
                                title="Institutional Role Wallets"
                                sub="Custodian · Transfer Agent · Registrar"
                            />
                            <SubstrateArrow label="role-to-role lifecycle protocol" />
                            <SubstrateLayer
                                title="KATLAS Control Decision Envelope"
                                sub="Authority · Policy · Evidence references · Outcome"
                                accent
                            />
                            <SubstrateArrow label="shared proof and verified receipt" />
                            <SubstrateLayer
                                title="Execution and standards adapters"
                                sub="Token action · Register update · ISO 20022-oriented workflow"
                            />
                        </div>
                        <p className="mt-4 text-xs leading-relaxed text-slate-500">
                            Private evidence remains with the accountable institution. The
                            shared control flow carries only the minimum structured
                            governance data needed for the authorised handoff.
                        </p>
                    </div>
                </div>
            </section>

            {/* Why KATLAS */}
            <section className="mt-16" data-testid="why-katlas">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    Why this is more than a control dashboard
                </h2>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-2">
                    {WHY.map((w) => (
                        <div key={w.title} className="bg-white p-6">
                            <div className="flex items-start gap-3">
                                <w.icon className="mt-0.5 h-5 w-5 shrink-0 text-blue-950" strokeWidth={1.5} />
                                <div>
                                    <h3 className="text-base font-medium text-slate-900">{w.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{w.body}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* The governed handoff (concept) */}
            <section className="mt-16 rounded-sm border border-slate-200 bg-white p-8" data-testid="handoff-concept">
                <p className="text-xs font-mono uppercase tracking-[0.14em] text-slate-400">
                    Runtime operational governance
                </p>
                <h2 className="mt-3 text-2xl font-medium tracking-tight text-slate-900">
                    The token action is downstream of the governed institutional decision
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                    Each market participant operates through an Institutional Role Wallet -
                    a governed identity and bounded-authority container, not a consumer
                    crypto wallet. The control follows the governed handoff, not the token.
                </p>
                <div className="mt-6 flex flex-col gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                    {[
                        ["Seller Custodian Role Wallet", "requests governed transfer"],
                        ["Transfer Agent Role Wallet", "validates authority and policy"],
                        ["KATLAS Governance Node", "signs and verifies decision receipt"],
                        ["Synthetic Execution Adapter", "acts only on approved instruction"],
                        ["Registrar Role Wallet", "receives governed register-update event"],
                    ].map(([w, s], i) => (
                        <div key={w} className="flex items-center gap-4 bg-white px-5 py-3">
                            <span className="font-mono text-xs text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-sm font-medium text-slate-900">{w}</span>
                            <span className="ml-auto text-xs text-slate-500">↓ {s}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* About / TRL */}
            <section className="mt-16 rounded-sm border border-slate-200 bg-white p-8">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    About the demonstrator
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                    Existing KATLAS governance components have been demonstrated in live
                    sandboxes and client PoC/MVP environments. For this specific
                    digital-assets control use case:
                </p>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-2">
                    <div className="bg-white p-5">
                        <p className="font-mono text-xs uppercase tracking-wide text-slate-400">Starting TRL</p>
                        <p className="mt-1 text-lg font-medium text-slate-900">TRL 4</p>
                        <p className="mt-1 text-sm text-slate-600">
                            Component integration and representative technical validation.
                        </p>
                    </div>
                    <div className="bg-white p-5">
                        <p className="font-mono text-xs uppercase tracking-wide text-slate-400">Target TRL at completion</p>
                        <p className="mt-1 text-lg font-medium text-slate-900">TRL 5</p>
                        <p className="mt-1 text-sm text-slate-600">
                            Integrated prototype validated in a controlled synthetic
                            digital-assets environment.
                        </p>
                    </div>
                </div>
                <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-500">
                    This sandbox validates an integrated, synthetic control model for
                    tokenised-asset actions. It does not represent a production
                    financial-market deployment, live settlement, live custody or
                    regulatory approval.
                </p>
            </section>
        </Layout>
    );
}

function SubstrateItem({ term, desc }) {
    return (
        <div>
            <dt className="text-sm font-medium text-slate-900">{term}</dt>
            <dd className="mt-0.5 text-sm leading-relaxed text-slate-600">{desc}</dd>
        </div>
    );
}

function SubstrateLayer({ title, sub, accent }) {
    return (
        <div className={`p-4 ${accent ? "bg-blue-950" : "bg-white"}`}>
            <p className={`text-sm font-medium ${accent ? "text-white" : "text-slate-900"}`}>{title}</p>
            <p className={`mt-0.5 font-mono text-[11px] tracking-tight ${accent ? "text-blue-200" : "text-slate-400"}`}>
                {sub}
            </p>
        </div>
    );
}

function SubstrateArrow({ label }) {
    return (
        <div className="flex items-center gap-2 bg-slate-50 px-4 py-1.5">
            <span className="text-slate-400">↓</span>
            <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
        </div>
    );
}
